// /combos — Combo Management (PAGES.md §3.95)
//
// Manage combo/meal deal configurations. RESTAURANT only — OWNER+ (50).
// Traceability: FR-ORD-047, FR-ORD-048

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Badge,
  Select,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  ConfirmDialog,
  StatCard,
  DataTable,
  Pagination,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { comboService, ROLE_LEVELS } from '@/services/combo.service';
import type { ComboWithItems, ComboStats } from '@/services/combo.service';
import { deriveAvgSavings } from './combo-stats';
import { useAuthStore } from '@/stores/use-auth-store';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { GLASS, GLASS_FILTER, GLASS_INTERACTIVE, cn } from '@/constants/design-tokens';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { ApiError } from '@/lib/api-client';
import { PlusIcon, PencilIcon, TrashIcon, SearchIcon, PackageIcon } from './icons';

// ─── Helpers ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: '', label: 'All Combos' },
  { value: 'current', label: 'Active today' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'past', label: 'Finished (past)' },
  { value: 'inactive', label: 'Disabled' },
];

function fmtComboDate(s: string): string {
  return new Date(`${s.slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

function formatAvailability(combo: ComboWithItems): string {
  const time =
    combo.availability_start || combo.availability_end
      ? `${combo.availability_start?.slice(0, 5) ?? '00:00'}–${combo.availability_end?.slice(0, 5) ?? '23:59'}`
      : null;
  let dates: string | null = null;
  if (combo.valid_from && combo.valid_until) dates = `${fmtComboDate(combo.valid_from)} – ${fmtComboDate(combo.valid_until)}`;
  else if (combo.valid_from) dates = `From ${fmtComboDate(combo.valid_from)}`;
  else if (combo.valid_until) dates = `Until ${fmtComboDate(combo.valid_until)}`;
  if (!time && !dates) return 'Always';
  return [dates, time].filter(Boolean).join(' · ');
}

function savingsBadgeVariant(pct: number): 'success' | 'warning' | 'danger' {
  if (pct > 15) return 'success';
  if (pct >= 5) return 'warning';
  return 'danger';
}

/** Local calendar day as 'YYYY-MM-DD' for comparing against the validity window. */
function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Where a combo sits relative to its validity date window:
 * - `finished`  — its valid_until is in the past (the deal is over)
 * - `upcoming`  — its valid_from is in the future (not started yet)
 * - `current`   — valid today (or no date bound)
 */
export function comboValidity(combo: ComboWithItems, today: string = todayStr()): 'finished' | 'upcoming' | 'current' {
  const validUntil = combo.valid_until?.slice(0, 10);
  const validFrom = combo.valid_from?.slice(0, 10);
  if (validUntil && validUntil < today) return 'finished';
  if (validFrom && validFrom > today) return 'upcoming';
  return 'current';
}

// Validity-category filters (resolved client-side from the date window) vs the
// server-side `is_active` filters.
const VALIDITY_CATEGORIES = ['current', 'upcoming', 'past'] as const;

function matchesValidityCategory(combo: ComboWithItems, cat: string, today: string): boolean {
  const v = comboValidity(combo, today);
  if (cat === 'past') return v === 'finished';
  if (cat === 'upcoming') return v === 'upcoming';
  if (cat === 'current') return v === 'current' && combo.is_active; // active today
  return true;
}

function comboStatusVariant(combo: ComboWithItems, today: string = todayStr()): 'active' | 'draft' | 'scheduled' | 'voided' {
  if (!combo.is_active) return 'draft';
  const v = comboValidity(combo, today);
  if (v === 'finished') return 'voided';
  if (v === 'upcoming') return 'scheduled';
  return combo.status === 'scheduled' ? 'scheduled' : 'active';
}

function comboStatusLabel(combo: ComboWithItems, today: string = todayStr()): string {
  if (!combo.is_active) return 'Inactive';
  const v = comboValidity(combo, today);
  if (v === 'finished') return 'Finished';
  if (v === 'upcoming') return 'Scheduled';
  return combo.status === 'scheduled' ? 'Scheduled' : 'Active';
}

// ─── Mobile Combo Card ─────────────────────────────────────────────────────

function ComboMobileCard({ combo, onEdit, onDelete, canDelete }: {
  combo: ComboWithItems;
  onEdit: () => void;
  onDelete: () => void;
  canDelete: boolean;
}) {
  const currency = useTenantCurrency();
  return (
    <div className={cn(GLASS_INTERACTIVE, 'p-4 animate-slide-up')} onClick={onEdit} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onEdit(); }}
      aria-label={`Edit combo ${combo.name}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-bold text-neutral-900 dark:text-neutral-100 truncate">{combo.name}</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 font-mono tabular-nums">
            {combo.items.length} item{combo.items.length !== 1 ? 's' : ''} · {formatAvailability(combo)}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge variant={savingsBadgeVariant(combo.savings_percentage)} size="sm">
            {combo.savings_percentage.toFixed(1)}%
          </Badge>
          <Badge variant={comboStatusVariant(combo)} size="sm" dot>{comboStatusLabel(combo)}</Badge>
        </div>
      </div>
      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-3">
          <span className="text-xs text-neutral-400 dark:text-neutral-500 line-through font-mono tabular-nums">{formatPaise(combo.individual_total, currency)}</span>
          <span className="text-sm font-bold text-neutral-900 dark:text-neutral-100 font-mono tabular-nums">{formatPaise(combo.combo_price, currency)}</span>
        </div>
        {canDelete && (
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="text-danger-600 hover:text-danger-700 hover:bg-danger-50 dark:hover:bg-danger-900/20"
            aria-label={`Delete ${combo.name}`}>
            <TrashIcon />
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

function ComboListContent() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);
  const role = useAuthStore((s) => s.profile?.role ?? '');
  const canDelete = (ROLE_LEVELS[role] ?? 0) >= ROLE_LEVELS.OWNER;

  const urlSearch = searchParams.get('search') ?? '';
  const urlStatus = searchParams.get('status') ?? '';
  const urlPage = Number(searchParams.get('page') ?? '1');

  const [search, setSearch] = useState(urlSearch);
  const [statusFilter, setStatusFilter] = useState(urlStatus);
  const [page, setPage] = useState(urlPage);
  const [combos, setCombos] = useState<ComboWithItems[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<ComboStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [deleteTarget, setDeleteTarget] = useState<ComboWithItems | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const searchRef = useRef<HTMLInputElement>(null);
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  useEffect(() => {
    const params: Record<string, string> = {};
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;
    if (page > 1) params.page = String(page);
    setSearchParamsRef.current(params, { replace: true });
  }, [search, statusFilter, page]);

  const fetchCombos = useCallback(async () => {
    setIsLoading(true);
    try {
      // Validity categories (current/upcoming/past) are derived client-side
      // from the date window, so fetch unfiltered (large page) and filter below.
      const isValidityFilter = (VALIDITY_CATEGORIES as readonly string[]).includes(statusFilter);
      // The "Avg Savings" KPI comes from the tenant-wide `/combos/stats`
      // aggregate (matching the same server-side filters as the list) so it
      // reflects every combo and stays constant across pages (GitHub issue #286).
      const [result, statsResult] = await Promise.all([
        comboService.list({
          location_id: selectedLocationId ?? undefined,
          status: isValidityFilter ? undefined : (statusFilter || undefined),
          search: search || undefined,
          page: isValidityFilter ? 1 : page,
          limit: isValidityFilter ? 200 : PAGE_SIZE,
        }),
        comboService.getStats({
          location_id: selectedLocationId ?? undefined,
          status: isValidityFilter ? undefined : (statusFilter || undefined),
          search: search || undefined,
        }),
      ]);
      setCombos(result.data);
      setTotal(result.meta?.total ?? result.data.length);
      setStats(statsResult);
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to load combos.', variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [selectedLocationId, statusFilter, search, page, toast]);

  useEffect(() => { fetchCombos(); }, [fetchCombos]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      if (e.key === 'n') { e.preventDefault(); navigate('/combos/new'); }
      else if (e.key === '/') { e.preventDefault(); searchRef.current?.focus(); }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await comboService.deactivate(deleteTarget.id);
      toast({ title: `"${deleteTarget.name}" deleted`, variant: 'success' });
      setDeleteTarget(null);
      fetchCombos();
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to delete combo.', variant: 'error' });
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTarget, fetchCombos, toast]);

  const today = todayStr();
  // Active today = enabled AND inside its validity window (or no date bound).
  const activeTodayCount = combos.filter((c) => c.is_active && comboValidity(c, today) === 'current').length;
  // Upcoming = validity window starts in the future.
  const upcomingCount = combos.filter((c) => comboValidity(c, today) === 'upcoming').length;
  // Finished = validity window has ended (past), regardless of the toggle.
  const finishedCount = combos.filter((c) => comboValidity(c, today) === 'finished').length;
  // Avg Savings is whole-menu (from `/combos/stats`), not the current page slice,
  // so it stays constant as you paginate (GitHub issue #286).
  const avgSavings = deriveAvgSavings(stats);

  // Apply the client-side validity-category filter for display.
  const isValidityFilter = (VALIDITY_CATEGORIES as readonly string[]).includes(statusFilter);
  const displayedCombos = isValidityFilter
    ? combos.filter((c) => matchesValidityCategory(c, statusFilter, today))
    : combos;

  const columns: ColumnDef<ComboWithItems>[] = useMemo(() => [
    {
      key: 'name', header: 'Combo Name',
      render: (_v: unknown, row: ComboWithItems) => (
        <button type="button" onClick={() => navigate(`/combos/${row.id}`)}
          className="font-bold text-neutral-900 dark:text-neutral-100 hover:text-violet-600 dark:hover:text-violet-400 transition-colors text-left">
          {row.name}
        </button>
      ),
    },
    {
      key: 'items', header: 'Items', numeric: true,
      render: (_v: unknown, row: ComboWithItems) => (
        <span className="font-mono tabular-nums text-neutral-700 dark:text-neutral-300">
          {row.items.length} <span className="text-xs text-neutral-400">item{row.items.length !== 1 ? 's' : ''}</span>
        </span>
      ),
    },
    {
      key: 'individual_total', header: 'Ind. Total', numeric: true,
      render: (_v: unknown, row: ComboWithItems) => (
        <span className="font-mono tabular-nums text-neutral-500 dark:text-neutral-400 line-through">{formatPaise(row.individual_total, currency)}</span>
      ),
    },
    {
      key: 'combo_price', header: 'Combo Price', numeric: true,
      render: (_v: unknown, row: ComboWithItems) => (
        <span className="font-mono tabular-nums font-bold text-neutral-900 dark:text-neutral-100">{formatPaise(row.combo_price, currency)}</span>
      ),
    },
    {
      key: 'savings', header: 'Savings',
      render: (_v: unknown, row: ComboWithItems) => (
        <Badge variant={savingsBadgeVariant(row.savings_percentage)} size="sm">
          {row.savings_percentage.toFixed(1)}%
        </Badge>
      ),
    },
    {
      key: 'availability', header: 'Availability',
      render: (_v: unknown, row: ComboWithItems) => (
        <span className="text-sm text-neutral-600 dark:text-neutral-400">{formatAvailability(row)}</span>
      ),
    },
    {
      key: 'is_active', header: 'Status',
      render: (_v: unknown, row: ComboWithItems) => (
        <Badge variant={comboStatusVariant(row)} size="sm" dot>{comboStatusLabel(row)}</Badge>
      ),
    },
    {
      key: 'actions', header: '',
      render: (_v: unknown, row: ComboWithItems) => (
        <div className="flex items-center gap-1 justify-end">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/combos/${row.id}`)} aria-label={`Edit ${row.name}`}>
            <PencilIcon />
          </Button>
          {canDelete && (
            <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(row)}
              className="text-danger-600 hover:text-danger-700 hover:bg-danger-50 dark:hover:bg-danger-900/20"
              aria-label={`Delete ${row.name}`}>
              <TrashIcon />
            </Button>
          )}
        </div>
      ),
    },
  ], [navigate, canDelete, currency]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <PageContainer
      title="Combos"
      subtitle={`${total} combo${total !== 1 ? 's' : ''}`}
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Combos' }]}
      actions={
        canDelete && (
          <Button variant="primary" size="sm" onClick={() => navigate('/combos/new')}>
            <PlusIcon /><span className="ml-1.5">Create Combo</span>
          </Button>
        )
      }
    >
      <div className="animate-fade-in">
        {/* KPI Stats — the three validity categories (current/upcoming/past). */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div className="animate-slide-up">
            <StatCard title="Total Combos" value={String(total)} icon={<PackageIcon className="h-4 w-4" />} />
          </div>
          <div className="animate-slide-up" style={{ animationDelay: '50ms' }}>
            <StatCard title="Active Today" value={String(activeTodayCount)} icon={<PackageIcon className="h-4 w-4" />} />
          </div>
          <div className="animate-slide-up" style={{ animationDelay: '100ms' }}>
            <StatCard title="Upcoming" value={String(upcomingCount)} icon={<PackageIcon className="h-4 w-4" />} />
          </div>
          <div className="animate-slide-up" style={{ animationDelay: '150ms' }}>
            <StatCard title="Finished" value={String(finishedCount)} icon={<PackageIcon className="h-4 w-4" />} />
          </div>
          <div className="animate-slide-up" style={{ animationDelay: '200ms' }}>
            <StatCard title="Avg Savings" value={`${avgSavings}%`} icon={<PackageIcon className="h-4 w-4" />} />
          </div>
        </div>

        {/* Filter Bar */}
        <div className={cn(GLASS_FILTER, 'p-4 mb-6')}>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-neutral-400 dark:text-neutral-500"><SearchIcon /></div>
              <input ref={searchRef} type="text" role="searchbox" value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search combos... (press / to focus)"
                className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white/60 dark:bg-neutral-800/60 text-sm text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 dark:placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-violet-500 transition-shadow"
                aria-label="Search combos" />
            </div>
            <div className="w-36">
              <Select options={STATUS_OPTIONS} value={statusFilter}
                onChange={(val) => { setStatusFilter(typeof val === 'string' ? val : val[0] ?? ''); setPage(1); }}
                placeholder="All Statuses" clearable />
            </div>
          </div>
        </div>

        {/* Desktop Table */}
        <div className={cn(GLASS, 'overflow-hidden hidden sm:block')}>
          {isLoading ? (
            <div className="p-6 space-y-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex gap-4">
                  <Skeleton variant="text" width="20%" height={18} />
                  <Skeleton variant="text" width="8%" height={18} />
                  <Skeleton variant="text" width="12%" height={18} />
                  <Skeleton variant="text" width="12%" height={18} />
                  <Skeleton variant="rect" width={55} height={24} />
                  <Skeleton variant="text" width="12%" height={18} />
                  <Skeleton variant="rect" width={55} height={24} />
                  <Skeleton variant="rect" width={64} height={24} />
                </div>
              ))}
            </div>
          ) : displayedCombos.length === 0 ? (
            <div className="p-12">
              <EmptyState variant="inline" icon={<PackageIcon />}
                title={isValidityFilter ? 'No combos in this category' : 'No combos created yet'}
                description={isValidityFilter ? 'Try a different category or clear the filter.' : 'Bundle menu items together at a special price'}
                action={canDelete && !isValidityFilter ? { label: 'Create Combo', onClick: () => navigate('/combos/new') } : undefined} />
            </div>
          ) : (
            <DataTable<ComboWithItems> columns={columns} data={displayedCombos} />
          )}
        </div>

        {/* Mobile Card List */}
        <div className="block sm:hidden space-y-3">
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className={cn(GLASS, 'p-4')}>
                  <Skeleton variant="text" width="60%" height={18} />
                  <Skeleton variant="text" width="40%" height={14} className="mt-2" />
                  <Skeleton variant="text" width="30%" height={18} className="mt-3" />
                </div>
              ))}
            </div>
          ) : displayedCombos.length === 0 ? (
            <div className={cn(GLASS, 'p-12')}>
              <EmptyState variant="inline" icon={<PackageIcon />}
                title={isValidityFilter ? 'No combos in this category' : 'No combos created yet'}
                description={isValidityFilter ? 'Try a different category or clear the filter.' : 'Bundle menu items together at a special price'}
                action={canDelete && !isValidityFilter ? { label: 'Create Combo', onClick: () => navigate('/combos/new') } : undefined} />
            </div>
          ) : (
            displayedCombos.map((combo) => (
              <ComboMobileCard key={combo.id} combo={combo} canDelete={canDelete}
                onEdit={() => navigate(`/combos/${combo.id}`)}
                onDelete={() => setDeleteTarget(combo)} />
            ))
          )}
        </div>

        {/* Pagination — only for the server-paginated (non-validity) views. */}
        {!isValidityFilter && totalPages > 1 && <div className="mt-4"><Pagination currentPage={page} totalPages={totalPages} totalItems={total} pageSize={PAGE_SIZE} onPageChange={setPage} /></div>}
      </div>

      <ConfirmDialog open={deleteTarget !== null} title={`Delete "${deleteTarget?.name ?? ''}"?`}
        description="This will deactivate the combo. It will no longer appear on the POS menu."
        confirmLabel="Delete" variant="danger" loading={isDeleting}
        onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />
    </PageContainer>
  );
}

export function ComboListPage() {
  return <ErrorBoundary><ComboListContent /></ErrorBoundary>;
}
